/**
 * Short, bounded retry for loads that fail because the API process is down
 * (cargo-watch restart, bad gateway). A real 401/403 is not transient.
 */

export const TRANSIENT_ATTEMPT_BUDGET = 24;
const BACKOFF_BASE_MS = 500;
const BACKOFF_CAP_MS = 3000;

export interface TransientUnavailable extends Error {
  transientUnavailable: true;
  status: number | null;
}

export function transientUnavailable(status: number | null): TransientUnavailable {
  const error = new Error('Error connecting to server');
  error.name = 'TransientUnavailableError';
  return Object.assign(error, {
    transientUnavailable: true as const,
    status,
  });
}

export function isTransientUnavailable(error: unknown): error is TransientUnavailable {
  return (
    error instanceof Error &&
    'transientUnavailable' in error &&
    (error as { transientUnavailable?: unknown }).transientUnavailable === true
  );
}

/** 0 = browser "failed to fetch" with no HTTP status. 502/503/504 = gateway/proxy while the process is down. */
export function isTransientHttpStatus(status: number): boolean {
  return status === 0 || status === 502 || status === 503 || status === 504;
}

/**
 * Network failure from fetch(): TypeError "Failed to fetch", an abort from our
 * own timeout, or an error object with an empty message / status 0.
 * 401/403 are HTTP responses, not network errors.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (isTransientUnavailable(error)) return true;
  if (error instanceof TypeError) return true;
  if (typeof error !== 'object' || error === null) return false;

  const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
  const message = 'message' in error && typeof error.message === 'string' ? error.message : '';
  if (name === 'AbortError' || name === 'NetworkError' || name === 'TypeError') return true;
  if (
    message === 'Failed to fetch' ||
    message === 'Load failed' ||
    message === 'Network request failed' ||
    message.toLowerCase().includes('networkerror')
  ) {
    return true;
  }
  if ('status' in error && typeof error.status === 'number' && isTransientHttpStatus(error.status)) {
    return true;
  }
  // Empty network error: fetch failed without a message or status text.
  if (message === '' && (name === '' || name === 'Error')) return true;
  return false;
}

export function transientBackoffMs(retryIndex: number): number {
  const exponential = BACKOFF_BASE_MS * 2 ** Math.max(0, retryIndex - 1);
  return Math.min(exponential, BACKOFF_CAP_MS);
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Run `operation` until it resolves, the error is not retryable, or the attempt
 * budget is exhausted. `retryIndex` passed to `backoffMs` starts at 1.
 */
export async function withTransientRetries<T>(
  operation: () => Promise<T>,
  isRetryable: (error: unknown) => boolean,
  options?: {
    attempts?: number;
    backoffMs?: (retryIndex: number) => number;
    sleep?: (ms: number) => Promise<void>;
  }
): Promise<T> {
  const attempts = options?.attempts ?? TRANSIENT_ATTEMPT_BUDGET;
  const backoffMs = options?.backoffMs ?? transientBackoffMs;
  const sleep = options?.sleep ?? defaultSleep;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (!isRetryable(error) || attempt >= attempts - 1) {
        throw error;
      }
      await sleep(backoffMs(attempt + 1));
    }
  }

  throw lastError instanceof Error ? lastError : transientUnavailable(null);
}
